import { useEffect, useMemo, useState } from "react";
import { MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { SectionHeading } from "@/components/Brand";

const WHATSAPP = "17879388834";
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SIZES = [
  ["2x2", 2, 2, true],
  ["4x6", 4, 6, true],
  ["5x7", 5, 7, false],
  ["8x10", 8, 10, true],
  ["11x14", 11, 14, false],
  ["20x30", 20, 30, false],
];

const FRAMES = [
  ["black", "#1C1C1C", "linear-gradient(145deg,#2b2b2b,#0f0f0f)"],
  ["wood", "#7A4B21", "linear-gradient(145deg,#a9713a,#5e3616)"],
  ["gold", "#C69A3B", "linear-gradient(145deg,#e5c477,#9c7222)"],
];

export default function PhotoPackages() {
  const { t } = useI18n();
  const [qty, setQty] = useState({});
  const [portrait, setPortrait] = useState(true);
  const [pricing, setPricing] = useState({ enabled: false, mode: "from", prices: {} });

  useEffect(() => {
    api.get("/print-prices").then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  const priceOf = (key) => (pricing.enabled ? Number(pricing.prices?.[key]) || 0 : 0);
  const money = (n) => `$${n.toFixed(2)}`;

  const chosen = useMemo(
    () => SIZES.filter(([key]) => Number(qty[key]) > 0),
    [qty]
  );

  const total = chosen.reduce((sum, s) => sum + priceOf(s[0]) * Number(qty[s[0]] || 0), 0);

  const label = ([key, a, b]) => (portrait || a === b ? key : `${b}x${a}`);

  const waHref = useMemo(() => {
    const lines = chosen.map((s) => {
      const p = priceOf(s[0]);
      return `• ${label(s)} — ${qty[s[0]]}${p ? ` (${money(p)} ${t("prints.each")})` : ""}`;
    });
    const tail = total ? `\n${t("prints.total")}: ${money(total)}\n` : "";
    const msg = `${t("prints.msgIntro")}\n${lines.join("\n")}\n${tail}\n${t("prints.msgTail")}`;
    return `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`;
  }, [chosen, qty, portrait, pricing, total, t]);

  const bump = (key, delta) =>
    setQty((q) => ({ ...q, [key]: Math.max(0, Math.min(99, (Number(q[key]) || 0) + delta)) }));

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-8 lg:px-12 lg:py-20" data-testid="prints-page">
      <SectionHeading label={t("brand.positioning")} title={t("prints.title")} sub={t("prints.sub")} />

      <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="prints-orientation">
        <span className="label-mono">{t("prints.orientation")}</span>
        {[[true, "lab.portrait"], [false, "lab.landscape"]].map(([val, key]) => (
          <button
            key={String(val)}
            type="button"
            data-testid={`prints-orient-${val ? "portrait" : "landscape"}`}
            onClick={() => setPortrait(val)}
            className={`min-h-[42px] border px-5 text-xs transition-colors duration-300 ${
              portrait === val
                ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]"
                : "border-[#C69A3B] text-[#8F3329] hover:bg-[#C69A3B]/15"
            }`}
          >
            {t(key)}
          </button>
        ))}
      </div>

      <div className="mt-10 grid gap-7 sm:grid-cols-2 lg:grid-cols-3" data-testid="prints-grid">
        {SIZES.map((size) => {
          const [key, a, b, popular] = size;
          const w = portrait ? a : b;
          const h = portrait ? b : a;
          const count = Number(qty[key]) || 0;
          return (
            <article
              key={key}
              data-testid={`prints-card-${key}`}
              className={`flex flex-col border bg-white p-6 transition-shadow duration-300 hover:shadow-[0_18px_40px_-28px_rgba(37,37,37,0.5)] ${
                count ? "border-[#8F3329]" : "border-[#C69A3B]/40"
              }`}
            >
              <div className="flex items-start justify-between">
                <h3 className="font-serif-display text-3xl text-[#8F3329]">{label(size)}</h3>
                {popular && <span className="label-mono text-[#C69A3B]">{t("prints.popular")}</span>}
              </div>
              <div className="mt-5 grid h-[150px] place-items-center bg-[#F4F1EA]">
                <div
                  className="border border-[#C69A3B]/60 bg-[#DED6C6]"
                  style={{ width: `${(w / Math.max(w, h)) * 120}px`, height: `${(h / Math.max(w, h)) * 120}px` }}
                />
              </div>
              <p className="mt-4 text-sm text-[#5A5752]" data-testid={`prints-price-${key}`}>
                {priceOf(key)
                  ? `${pricing.mode === "from" ? `${t("prints.from")} ` : ""}${money(priceOf(key))} ${t("prints.each")}`
                  : t("prints.quote")}
              </p>
              <div className="mt-5 flex items-center gap-3">
                <button
                  type="button"
                  data-testid={`prints-minus-${key}`}
                  onClick={() => bump(key, -1)}
                  className="min-h-[40px] w-10 border border-[#C69A3B] text-[#8F3329]"
                >
                  −
                </button>
                <span className="label-mono w-8 text-center" data-testid={`prints-qty-${key}`}>{count}</span>
                <button
                  type="button"
                  data-testid={`prints-plus-${key}`}
                  onClick={() => bump(key, 1)}
                  className="min-h-[40px] w-10 border border-[#C69A3B] text-[#8F3329]"
                >
                  +
                </button>
                <span className="text-xs text-[#5A5752]">{t("prints.qty")}</span>
              </div>
            </article>
          );
        })}
      </div>

      <section className="mt-16" data-testid="prints-samples">
        <p className="label-mono">{t("prints.samples")}</p>
        <div className="gold-rule mt-5 w-24" />
        <p className="mt-5 max-w-2xl text-sm text-[#5A5752]">{t("prints.samplesHelp")}</p>
        <div className="mt-8 grid gap-8 sm:grid-cols-3">
          {FRAMES.map(([key]) => (
            <figure key={key} data-testid={`prints-sample-${key}`}>
              <img
                src={`${API_BASE}/api/frame-samples/${key}`}
                alt={t(`lab.frame.${key}`)}
                loading="lazy"
                className="w-full border border-[#C69A3B]/20"
              />
              <figcaption className="label-mono mt-3">{t(`lab.frame.${key}`)}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      <div className="mt-12 border border-[#C69A3B]/40 bg-[#FAFAF7] p-7" data-testid="prints-order-box">
        <p className="label-mono">{t("prints.frames")}</p>
        <div className="mt-4 flex flex-wrap gap-5">
          {FRAMES.map(([key, , grad]) => (
            <span key={key} className="flex items-center gap-2 text-xs text-[#5A5752]" data-testid={`prints-frame-${key}`}>
              <span className="h-8 w-8 border border-black/10" style={{ background: grad }} />
              {t(`lab.frame.${key}`)}
            </span>
          ))}
        </div>
        <p className="mt-6 max-w-3xl text-sm text-[#5A5752]" data-testid="prints-note">{t("prints.note")}</p>
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <a
            href={chosen.length ? waHref : undefined}
            target="_blank"
            rel="noreferrer"
            aria-disabled={!chosen.length}
            data-testid="prints-order-whatsapp"
            className={`inline-flex min-h-[50px] items-center gap-2 border px-7 text-sm transition-colors duration-300 ${
              chosen.length
                ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7] hover:bg-[#252525]"
                : "pointer-events-none border-[#C69A3B]/50 text-[#5A5752] opacity-60"
            }`}
          >
            <MessageCircle size={16} /> {t("prints.order")}
          </a>
          <span className="text-xs text-[#5A5752]" data-testid="prints-selected">
            {chosen.length
              ? `${t("prints.selected")}: ${chosen.map((s) => `${label(s)} ×${qty[s[0]]}`).join(" · ")}${total ? ` — ${t("prints.total")}: ${money(total)}` : ""}`
              : t("prints.none")}
          </span>
        </div>
      </div>
    </div>
  );
}
